import { z } from 'zod';
import { TIMEFRAME_LIST } from '../constants/index.js';

// ============================================================================
// Signal Create Schema
//
// Canonical validation contract for creating a trading signal. Used by both:
//   - apps/api/src/modules/signals/signals.routes.ts  (POST /)
//   - apps/api/src/modules/admin/analysisRun.routes.ts (POST /analysis-results/:id/create-signal)
//
// `narrative` is the canonical user-facing text field (min 10, max 5000).
// `analysis` is kept as an optional backwards-compat alias that maps to the
// existing DB column (`analysis` NOT NULL). Call `normalizeSignalCreateInput`
// after parsing to ensure `analysis` is always populated before persisting.
// ============================================================================

export const signalCreateSchema = z.object({
  // Asset is intentionally a free-form string — the signal system must accept
  // any tradeable symbol (Forex, Crypto, Stocks, Indices, ETFs, futures, …).
  // The previous enum constraint silently rejected stocks (NVDA, AAPL, …) and
  // produced confusing "invalid_enum_value" errors at the API boundary.
  asset: z.string().min(1).max(50),
  timeframe: z.enum(TIMEFRAME_LIST as [string, ...string[]]),
  direction: z.enum(['LONG', 'SHORT']),
  entryPrice: z.number().positive(),
  stopLoss: z.number().positive(),
  takeProfit1: z.number().positive(),
  takeProfit2: z.number().positive().optional(),
  takeProfit3: z.number().positive().optional(),
  riskReward: z.number().positive().optional(),
  confidence: z.number().int().min(0).max(100),
  /** Canonical user-facing text. Required — min 10, max 5000 chars. */
  narrative: z.string().min(10).max(5000),
  /** Backwards-compat alias for the DB `analysis` column. When omitted,
   *  `normalizeSignalCreateInput` fills it from `narrative`. */
  analysis: z.string().min(10).max(5000).optional(),
  /**
   * Optional free-text guidance written by the expert at broadcast time.
   * Propagated to all notification channels and the in-app Signals UI.
   * Max 500 chars. Not auto-translated — stays in whatever language the
   * expert wrote it in.
   */
  expertNotes: z.string().max(500).optional(),
  expiresAt: z.string().datetime().optional(),
  /**
   * When true AND the caller has admin/expert role, the signal is created
   * with status='approved' immediately and the broadcast fanout fires in the
   * same request. The route enforces role-gate server-side; plain users who
   * send this field have it silently ignored (status stays 'pending').
   */
  autoApprove: z.boolean().optional(),
});

export type SignalCreateInput = z.infer<typeof signalCreateSchema>;

/**
 * Ensures `analysis` is always present (required by the DB NOT NULL constraint)
 * by falling back to `narrative` when the caller did not supply it explicitly.
 *
 * Call this after `signalCreateSchema.parse()` and before persisting to the DB.
 */
export function normalizeSignalCreateInput(
  input: SignalCreateInput
): SignalCreateInput & { analysis: string } {
  return {
    ...input,
    analysis: input.analysis ?? input.narrative,
  };
}
